import {
	UploadFileArgs,
	useUploadFile,
} from '@dealstack/frontend/shared/components/DragAndDrop/useUploadFile';
import { ButtonsCenter } from '@dealstack/frontend/shared/components/fundament/Button/ButtonLayouts';
import {
	TemplateDownloadButton,
	UploadFileButton,
} from '@dealstack/frontend/shared/components/fundament/Button/CommonButtons';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { ProgressBar } from '@dealstack/frontend/shared/components/fundament/ProgressBar/ProgressBar';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { useTranslationDefault } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { MakeRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper/types';
import { UseMutationResult } from '@tanstack/react-query';
import { isString } from 'lodash';
import { memo, useEffect, useMemo, useState } from 'react';
import { DropzoneOptions, FileRejection, useDropzone } from 'react-dropzone';
import styled from 'styled-components';

interface Props extends DropzoneOptions {
	className?: string;
	/**
	 * Sometimes it may happen that we need to reset this component
	 * but we don't want to unmount and remount it.
	 * In such cases, just change refresh key that will restart this component
	 * to it's default state
	 */
	refreshKey?: unknown;
	onDrop?: (acceptedFiles: File[]) => void;
	onTemplateDownload?: () => void;
	downloadButton?: React.ReactElement;
	uploadFileRequest?: MakeRequest<UploadFileArgs>; // use it to auto upload files on drop with showing progress bar
	uploadFileQueryRequest?: UseMutationResult; // use it to auto upload files on drop with showing progress bar
	hideSelectedFilesList?: boolean;
	invalidTypeMessage?: string;
	description?: React.ReactElement | string;
	style?: React.CSSProperties;
}

const DragAndDrop = ({
	refreshKey,
	onDrop,
	uploadFileRequest,
	uploadFileQueryRequest,
	onTemplateDownload,
	className,
	hideSelectedFilesList,
	invalidTypeMessage,
	downloadButton,
	accept,
	description,
	style,
	...props
}: Props) => {
	const tDefault = useTranslationDefault();
	const [lastAcceptedFiles, setLastAcceptedFiles] = useState<File[]>();

	useEffect(() => {
		setLastAcceptedFiles(undefined);
		uploadFileRequest?.resetInstance();
		uploadFileQueryRequest?.reset();
	}, [refreshKey]);

	const shouldUploadOnDrop = !!uploadFileRequest || !!uploadFileQueryRequest;

	const { onUpload, percentComplete, isUploadError, isUploadSuccess } = useUploadFile({
		uploadFileRequest,
		uploadFileQueryRequest,
	});

	const uploadOnDrop = async (files: File[]) => {
		files.forEach((file) => onUpload(file));
	};

	const onFilesDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
		if (!acceptedFiles?.length || rejectedFiles?.length) {
			setLastAcceptedFiles(undefined);
			return;
		}

		setLastAcceptedFiles(acceptedFiles);
		if (shouldUploadOnDrop) {
			void uploadOnDrop(acceptedFiles);
		} else {
			onDrop && onDrop(acceptedFiles);
		}
	};
	const { getRootProps, getInputProps, open, fileRejections } = useDropzone({
		onDrop: onFilesDrop,
		noClick: true,
		onFileDialogOpen: () => setLastAcceptedFiles(undefined),
		accept,
		...props,
	});

	const showDefaultView = !lastAcceptedFiles?.length && !fileRejections?.length;
	const showInvalidTypeView = fileRejections?.some((rejection) =>
		rejection.errors.some((error) => error.code === 'file-invalid-type')
	);
	const showDroppedFilesList =
		!hideSelectedFilesList && !!lastAcceptedFiles?.length && !shouldUploadOnDrop;
	const showUploadView =
		!!lastAcceptedFiles?.length && shouldUploadOnDrop && !fileRejections?.length;

	const preparedDescription = useMemo(() => {
		if (!description) {
			return <Text variant="p-md">{tDefault('drag_drop_description')}</Text>;
		}
		if (isString(description)) {
			return <Text variant="p-md">{description}</Text>;
		}

		return description;
	}, [description]);

	const getUploadStatusText = () => {
		if (isUploadError) {
			return 'error_upload';
		}
		if (isUploadSuccess) {
			return 'successfully_uploaded';
		}
		return 'uploading_file';
	};

	return (
		<div className={className} {...getRootProps()} style={style}>
			<input {...getInputProps()} />
			{showDefaultView && preparedDescription}
			{showInvalidTypeView && (
				<ErrorText variant="p-lg" bold>
					{invalidTypeMessage}
				</ErrorText>
			)}

			{showUploadView && (
				<UploadView>
					<Text variant="p-md" bold>
						{tDefault(getUploadStatusText())}
					</Text>
					<FileName variant="p-md">{lastAcceptedFiles?.[0]?.name}</FileName>
					<ProgressBar percent={percentComplete} isError={isUploadError} />
				</UploadView>
			)}

			{showDroppedFilesList && (
				<FilesList>
					<Text variant="p-md" bold>
						Files added:
					</Text>
					<div>
						{lastAcceptedFiles.map((file) => (
							<FileListItem key={file.name}>
								<Icon variant="document" />
								<Text variant="p-md">{file.name}</Text>
							</FileListItem>
						))}
					</div>
				</FilesList>
			)}

			<ButtonsCenter>
				{downloadButton
					? downloadButton
					: !!onTemplateDownload && (
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.DRAG_AND_DROP_TEMPLATE_DOWNLOAD_BUTTON}
							>
								<TemplateDownloadButton onClick={onTemplateDownload} />
							</UIElementIdentifier>
						)}
				<UIElementIdentifier identifier={TEST_ELEMENT_IDS.DRAG_AND_DROP_UPLOAD_FILE_BUTTON}>
					<UploadFileButton onClick={open} />
				</UIElementIdentifier>
			</ButtonsCenter>
		</div>
	);
};

const UploadView = styled.div`
	width: 100%;
	${ProgressBar} {
		margin-top: 1em;
	}
`;

const FileName = styled(Text)`
	margin-left: 0.5rem;
`;

const FilesList = styled.div`
	margin-top: 1em;
	width: 100%;
	text-align: center;
`;

const FileListItem = styled.div`
	${Icon} {
		margin-right: 0.3em;
	}
`;

const ErrorText = styled(Text)`
	color: ${({ theme }) => theme.v2.color.error[600]} !important;
`;

const Memoized = memo(DragAndDrop);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	padding: 2rem;
	background-color: ${({ theme }) => theme.v2.color.secondary[200]};
	border: 1px dashed ${({ theme }) => theme.v2.color.secondary[800]};
	border-radius: 0.4rem;

	& > span {
		color: ${({ theme }) => theme.v2.color.neutral[1100]};
		white-space: pre-line;
	}

	button {
		margin-top: 1.5rem;
		color: ${({ theme }) => theme.v2.color.secondary[800]};
		background-color: ${({ theme }) => theme.v2.color.secondary[200]};

		&:last-of-type {
			border-color: ${({ theme }) => theme.v2.color.secondary[800]};
		}
	}
`;

export { Styled as DragAndDrop };
